import { Typography, TypographyType, UploadButton } from '@/components/common';
import { TooltipComponent } from '@/components/common/tooltip';
import { END_STATUSES_CASE } from '@/const/cases';
import { StatusTypes } from '@/interfaces';
import clsx from 'clsx';
import Image from 'next/image';
import * as React from 'react';

interface Props {
	children: React.ReactNode;
	className?: string;
	title: string;
	classNameTitle?: string;
	description?: string;
	icon?: string;
	titleComponentAux?: React.ReactNode;
	status?: StatusTypes;
	uploadButton?: boolean;
	clientApproveEmail?: boolean;
	buttonLabel?: string;
	buttonIcons?: string;
	isLoading?: boolean;
	file?: File;
	classNameContent?: string;
	classNameHeader?: string;
	iconWidth?: number;
	iconHeight?: number;
	titleTypography?: TypographyType;
	customButton?: React.ReactNode;
	tooltipContent?: React.ReactNode;
	tooltipLabel?: string | React.ReactNode;
	tooltipAlign?: 'center' | 'end' | 'start' | undefined;
	tooltipCustomLabel?: React.ReactNode;
	tooltipClassName?: string;
	stepName?: string;
	isCaseReadOnly?: boolean;
	setFile?: (value: File) => void;
	scroll?: boolean;
	classNameHeaderContent?: string;
	tooltipSide?: 'top' | 'right' | 'bottom' | 'left' | undefined;
}

export const CardContainer: React.FC<Props> = ({
	className,
	children,
	icon,
	titleComponentAux,
	title,
	classNameTitle,
	tooltipAlign,
	description,
	status,
	uploadButton,
	clientApproveEmail,
	buttonLabel,
	buttonIcons,
	isLoading,
	file,
	setFile,
	classNameContent,
	classNameHeader,
	classNameHeaderContent,
	titleTypography,
	customButton,
	tooltipContent,
	tooltipLabel,
	tooltipClassName,
	tooltipCustomLabel,
	stepName,
	iconWidth = 61,
	iconHeight = 48,
	isCaseReadOnly,
	scroll = true,
	tooltipSide,
}) => {
	const getIsDisabled = (): boolean | undefined => {
		return (
			status === 'Archived' ||
			status === 'Resolved Case' ||
			(status && END_STATUSES_CASE.includes(status))
		);
	};

	return (
		<div
			className={clsx(
				'bg-white rounded-3xl p-6 h-full w-full',
				{
					'!bg-bg-disabled border border-[#0D061614]':
						status === 'Declined' && stepName !== 'Application in review'
							? clientApproveEmail
							: getIsDisabled() || isCaseReadOnly,
				},
				className
			)}
		>
			<div
				className={clsx(
					'flex flex-col xl:flex-row xl:items-center xl:justify-between mb-4',
					classNameHeader
				)}
			>
				<div
					className={clsx('flex items-center gap-2', classNameHeaderContent)}
				>
					{icon && (
						<Image
							src={icon}
							alt="icon"
							width={iconWidth}
							height={iconHeight}
							className="self-start"
							style={{ height: `${iconHeight}px`, width: `${iconWidth}px` }}
							unoptimized
						/>
					)}
					<div className="3xl:ml-2">
						<Typography
							type={titleTypography || 'semibold-20'}
							className={clsx('text-black', classNameTitle)}
						>
							{title}
						</Typography>
						{description && (
							<Typography type="regular-14" className="opacity-60">
								{description}
							</Typography>
						)}
					</div>
					{titleComponentAux && titleComponentAux}
					{tooltipContent && (
						<TooltipComponent
							label={tooltipLabel}
							align={tooltipAlign}
							customLabel={tooltipCustomLabel}
							trigger={tooltipContent}
							className={tooltipClassName}
							classNameLabel="!text-left"
							side={tooltipSide}
						/>
					)}
				</div>
				{uploadButton && buttonLabel && setFile && (
					<UploadButton
						buttonIcons={buttonIcons}
						buttonLabel={buttonLabel}
						setFile={setFile}
						file={file}
						disabled={getIsDisabled() || isLoading}
					/>
				)}
				{customButton && customButton}
			</div>
			<div
				className={clsx(
					{ 'overflow-auto scroll-custom': scroll },
					'h-[154px]',
					classNameContent
				)}
			>
				{children}
			</div>
		</div>
	);
};
